/**
 * @file components/audit-tasks/DocReviewPanel.tsx
 * @description 稽核任務「開案文件審查」卡片（客戶 PPT S38，接入 PhasePreparation 階段 1）。
 *
 * 業務流：稽核員發起一輪審查（自動建【文件審查紀錄】表單，TOP＝D-ZHSR02-27）→
 * 填表後判定：通過（該輪關閉）／需補件（自動建【文件審查通知單】D-ZHSR02-28 並通知
 * 管理組轉達客戶）→ 收到補件後上傳關聯 → 可再發起新一輪審查直至通過。
 *
 * 功能：
 * - 案件附件比對區（序65）：列出本案 case_documents（受理階段客戶上傳的申請文件），
 *   稽核員可下載 / 預覽逐項比對，比對後生成文件審查記錄。
 * - 歷輪審查列表（輪次 / 結果 Tag / 時間 / 備註 / 表單鏈接）
 * - 「開始文件審查」按鈕（assignee 本人可見，且無審查中輪次）→ 建輪後跳表單填寫頁
 * - 每輪操作：「判定」Modal（通過 / 需補件 + 備註）；supplement 輪顯示通知單鏈接、
 *   「上傳稽核文件審查補件」（直傳 OSS 後即刻關聯，服務端合併去重）與補件檔案列表，
 *   每個補件檔可「替換」/「刪除」（序66，稽核員或管理組可操作）。
 *
 * ⚠️（序66）此【稽核文件審查補件】＝稽核員文件審查時「證據不足 / 不符」的客戶補件，
 * 與管理組派案文件點檢的【文件點檢補充】（序57）是兩個不同概念，UI 文案明確區分。
 *
 * 數據源：
 * - GET    /api/audit-tasks/:taskId/doc-reviews                     歷輪列表 + 案件附件（caseDocuments）
 * - POST   /api/audit-tasks/:taskId/doc-reviews                     發起新一輪
 * - POST   /api/audit-tasks/:taskId/doc-reviews/:id/decide          判定
 * - POST   /api/audit-tasks/:taskId/doc-reviews/:id/supplement      追加補件（合併去重）
 * - DELETE /api/audit-tasks/:taskId/doc-reviews/:id/supplement/:fid 移除某補件檔（序66）
 */

import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import { FileSearch, FolderOpen, Paperclip, RefreshCw } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AUDIT_DOC_REVIEW_RESULT_LABELS, userCan } from '@zhcb/shared';
import type {
  AuditDocReviewCaseDocument,
  AuditDocReviewItem,
  AuditDocReviewResult,
  AuditTaskDetail,
} from '@zhcb/shared';
import { FileLink } from '@/components/common/FileLink';
import { useFileMetas } from '@/hooks/useFileMetas';
import { apiClient, ApiError } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { useAuthStore } from '@/lib/auth-context';
import { message } from '@/lib/antd-app';

interface Props {
  task: AuditTaskDetail;
}

/** 判定 Modal 表單值 */
interface DecideFormValues {
  result: AuditDocReviewResult;
  resultNote?: string;
}

/** 結果 → Tag 顏色（null=審查中） */
function resultTag(result: AuditDocReviewResult | null) {
  if (result === null) return <Tag color="processing">審查中</Tag>;
  return (
    <Tag color={result === 'pass' ? 'green' : 'orange'}>
      {AUDIT_DOC_REVIEW_RESULT_LABELS[result]}
    </Tag>
  );
}

/** 將 ISO 時間字串格式化為「YYYY-MM-DD HH:mm」 */
function formatDateTime(iso: string): string {
  return dayjs(iso).format('YYYY-MM-DD HH:mm');
}

export function DocReviewPanel({ task }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  /** 取整個 user 對象，供 userCan 同時感知 capabilities（動態 RBAC）與 roles（回落）*/
  const authUser = useAuthStore((s) => s.user);
  /** 判定 Modal 針對的輪次（null = 關閉） */
  const [decideTarget, setDecideTarget] = useState<AuditDocReviewItem | null>(null);
  const [decideForm] = Form.useForm<DecideFormValues>();
  /** 「替換補件」用隱藏 file input（記住要替換的 reviewId + 舊 fileId） */
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<{ reviewId: number; oldFileId: number } | null>(null);

  // 歷輪審查（最早在前；輪次 = 序號 + 1）+ 案件附件比對清單（序65）
  const { data, isLoading } = useQuery<{
    items: AuditDocReviewItem[];
    caseDocuments: AuditDocReviewCaseDocument[];
  }>({
    queryKey: ['audit-doc-reviews', task.id],
    queryFn: () =>
      apiClient.get<{
        items: AuditDocReviewItem[];
        caseDocuments: AuditDocReviewCaseDocument[];
      }>(`/api/audit-tasks/${task.id}/doc-reviews`),
  });

  const items = data?.items ?? [];
  /** 本案申請文件附件（序65：要比對的案件附件） */
  const caseDocuments = data?.caseDocuments ?? [];
  /** 是否有審查中（未判定）的輪次：有則不可再建新輪 */
  const hasOpenRound = items.some((it) => it.result === null);
  /** 最新輪次已通過 → 開案文件審查完成 */
  const passed = items.length > 0 && items[items.length - 1]!.result === 'pass';
  /** 僅任務 assignee 本人可操作建輪 / 判定（後端亦強制校驗） */
  const isAssignee = userId != null && userId === task.assigneeId;
  /** 管理組（case.dispatch）：序66 補件上傳 / 替換 / 刪除也允許管理組操作 */
  const isManager = authUser ? userCan(authUser, 'case.dispatch') : false;
  /** 可操作補件（上傳 / 替換 / 刪除）：稽核員本人或管理組 */
  const canSupplement = isAssignee || isManager;

  /** 所有輪次補件檔案 id（批量解析檔名用；案件附件已自帶檔名，無需解析） */
  const allFileIds = items.flatMap((it) => it.supplementFileIds);
  const { metas } = useFileMetas(allFileIds);

  /** 刷新列表（判定 / 補件 / 建輪後） */
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['audit-doc-reviews', task.id] });

  /** 統一錯誤提示：後端業務錯誤碼 → 友善文案 */
  const showError = (err: unknown, fallback: string) => {
    const e = err as ApiError;
    message.error(e.message || fallback);
  };

  // 發起新一輪審查：建輪成功後直接跳表單填寫頁（?from=preparation 供返回定位）
  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ item: AuditDocReviewItem }>(`/api/audit-tasks/${task.id}/doc-reviews`, {}),
    onSuccess: (res) => {
      message.success('已建立文件審查，請填寫【文件審查紀錄】');
      invalidate();
      void navigate({
        to: '/audit-tasks/$taskId/form/$instanceId',
        params: { taskId: String(task.id), instanceId: String(res.item.reviewFormInstanceId) },
        search: { from: 'preparation' },
      });
    },
    onError: (err: unknown) => showError(err, '發起文件審查失敗，請稍後重試'),
  });

  // 判定（通過 / 需補件 + 備註）
  const decideMutation = useMutation({
    mutationFn: (values: DecideFormValues & { reviewId: number }) =>
      apiClient.post<{ item: AuditDocReviewItem }>(
        `/api/audit-tasks/${task.id}/doc-reviews/${values.reviewId}/decide`,
        { result: values.result, resultNote: values.resultNote?.trim() || undefined },
      ),
    onSuccess: (res) => {
      message.success(
        res.item.result === 'pass' ? '文件審查已通過' : '已判定需補件，並通知管理組轉達客戶',
      );
      setDecideTarget(null);
      decideForm.resetFields();
      invalidate();
    },
    onError: (err: unknown) => showError(err, '判定失敗，請稍後重試'),
  });

  // 追加補件（單檔上傳成功即關聯；服務端合併去重）
  const supplementMutation = useMutation({
    mutationFn: (payload: { reviewId: number; fileIds: number[] }) =>
      apiClient.post<{ item: AuditDocReviewItem }>(
        `/api/audit-tasks/${task.id}/doc-reviews/${payload.reviewId}/supplement`,
        { fileIds: payload.fileIds },
      ),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => showError(err, '補件關聯失敗，請稍後重試'),
  });

  // 移除某補件檔（序66：誤上傳可刪除；DELETE 僅解除關聯，不刪 files 實體）
  const removeSupplementMutation = useMutation({
    mutationFn: (payload: { reviewId: number; fileId: number }) =>
      apiClient.delete<{ item: AuditDocReviewItem }>(
        `/api/audit-tasks/${task.id}/doc-reviews/${payload.reviewId}/supplement/${payload.fileId}`,
      ),
    onSuccess: () => {
      message.success('已移除補件檔案');
      invalidate();
    },
    onError: (err: unknown) => showError(err, '移除補件失敗，請稍後重試'),
  });

  /** 刪除補件檔（二次確認後 DELETE） */
  const handleRemoveSupplement = (reviewId: number, fileId: number) => {
    Modal.confirm({
      title: '刪除此補件檔案？',
      content: '僅解除本輪補件關聯，不影響已上傳的檔案本身。',
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => removeSupplementMutation.mutateAsync({ reviewId, fileId }),
    });
  };

  /**
   * 替換補件檔（序66）：記住 reviewId + 舊 fileId，觸發隱藏 file input 選新檔；
   * 選檔後在 onReplaceFileChange 中「先上傳新檔關聯 → 再移除舊檔」完成替換。
   */
  const handleReplaceSupplement = (reviewId: number, oldFileId: number) => {
    replaceTargetRef.current = { reviewId, oldFileId };
    replaceInputRef.current?.click();
  };

  /** 隱藏 file input 的 change：執行「上傳新檔 → 移除舊檔」的替換序列 */
  const onReplaceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = replaceTargetRef.current;
    // 清空 input 值，允許連續選同名檔再次觸發 change
    e.target.value = '';
    if (!file || !target) return;
    replaceTargetRef.current = null;
    void (async () => {
      try {
        const uploaded = await uploadFile(file, { scope: 'case' });
        await supplementMutation.mutateAsync({
          reviewId: target.reviewId,
          fileIds: [uploaded.id],
        });
        // 新檔關聯成功後移除舊檔（若舊 = 新，跳過）
        if (uploaded.id !== target.oldFileId) {
          await removeSupplementMutation.mutateAsync({
            reviewId: target.reviewId,
            fileId: target.oldFileId,
          });
        }
        message.success(`已用「${file.name}」替換補件`);
      } catch (err) {
        // 業務必要：替換失敗輸出錯誤日誌，便於排查（presign / OSS PUT / confirm / 關聯 / 刪除）
        console.error('[DocReviewPanel] supplement replace failed', {
          filename: file.name,
          reviewId: target.reviewId,
          oldFileId: target.oldFileId,
          err,
        });
        message.error(`「${file.name}」替換失敗，請重試`);
      }
    })();
  };

  /** 補件上傳：直傳 OSS → 落庫拿 fileId → 立即關聯到該輪（合併去重） */
  const makeSupplementUploadRequest =
    (reviewId: number): NonNullable<UploadProps['customRequest']> =>
    (options) => {
      const file = options.file as File;
      void (async () => {
        try {
          const uploaded = await uploadFile(file, { scope: 'case' });
          await supplementMutation.mutateAsync({ reviewId, fileIds: [uploaded.id] });
          message.success(`「${file.name}」已上傳並關聯補件`);
        } catch (err) {
          // 業務必要：上傳/關聯失敗輸出錯誤日誌，便於排查（presign / OSS PUT / confirm / 關聯）
          console.error('[DocReviewPanel] supplement upload failed', {
            filename: file.name,
            reviewId,
            err,
          });
          message.error(`「${file.name}」上傳失敗，請重試`);
        }
      })();
    };

  const handleDecideSubmit = async () => {
    if (!decideTarget) return;
    try {
      const values = await decideForm.validateFields();
      decideMutation.mutate({ ...values, reviewId: decideTarget.id });
    } catch {
      /* 表單校驗失敗：antd 已在欄位下方展示錯誤，這裡不需額外處理 */
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <FileSearch size={14} />
          開案文件審查
          {passed && <Tag color="green">已通過</Tag>}
        </Space>
      }
      extra={
        isAssignee &&
        !hasOpenRound &&
        !passed && (
          <Button
            size="small"
            type="primary"
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            開始文件審查
          </Button>
        )
      }
      loading={isLoading}
    >
      {/* 序65：案件附件比對區——列出本案申請文件供逐項比對（下載 / 預覽） */}
      <Alert
        type="info"
        showIcon
        icon={<FolderOpen size={14} />}
        style={{ marginBottom: 12 }}
        message={
          <Space size={6}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              案件附件（要比對的申請文件）
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              共 {caseDocuments.length} 份
            </Typography.Text>
          </Space>
        }
        description={
          caseDocuments.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              本案尚無申請文件附件。請至案件詳情確認文件是否已上傳。
            </Typography.Text>
          ) : (
            <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 4 }}>
              {caseDocuments.map((doc) => (
                <Space key={doc.id} size={6} wrap>
                  <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                    {doc.docType}
                  </Tag>
                  <FileLink
                    fileId={doc.fileId}
                    filename={doc.filename ?? `檔案 #${doc.fileId}`}
                    sizeBytes={doc.sizeBytes ?? undefined}
                    compact
                  />
                  {doc.note && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {doc.note}
                    </Typography.Text>
                  )}
                </Space>
              ))}
            </Space>
          )
        }
      />

      {/* 序66：隱藏 file input，供「替換補件」選新檔（先上傳新檔關聯 → 再移除舊檔） */}
      <input
        ref={replaceInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={onReplaceFileChange}
      />

      {items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            isAssignee
              ? '尚未進行文件審查。點擊「開始文件審查」填寫【文件審查紀錄】。'
              : '尚未進行文件審查。'
          }
        />
      ) : (
        <Space
          direction="vertical"
          size={0}
          style={{ width: '100%' }}
          split={<Divider style={{ margin: '12px 0' }} />}
        >
          {items.map((it, idx) => (
            <div key={it.id}>
              {/* 輪次標頭：輪次 / 結果 / 時間 / 操作 */}
              <Space size={8} wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space size={8} wrap>
                  <Typography.Text strong>第 {idx + 1} 輪</Typography.Text>
                  {resultTag(it.result)}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {it.createdByName ?? (it.createdBy != null ? `#${it.createdBy}` : '—')} ·{' '}
                    {formatDateTime(it.createdAt)}
                  </Typography.Text>
                </Space>
                {isAssignee && it.result === null && (
                  <Button size="small" onClick={() => setDecideTarget(it)}>
                    判定
                  </Button>
                )}
              </Space>

              {/* 表單鏈接：審查紀錄（必有）/ 通知單（supplement 輪才有） */}
              <Space size={12} wrap style={{ marginTop: 6 }}>
                <Link
                  to="/audit-tasks/$taskId/form/$instanceId"
                  params={{
                    taskId: String(task.id),
                    instanceId: String(it.reviewFormInstanceId),
                  }}
                  search={{ from: 'preparation' }}
                  style={{ fontSize: 12 }}
                >
                  文件審查紀錄表單
                </Link>
                {it.noticeFormInstanceId != null && (
                  <Link
                    to="/audit-tasks/$taskId/form/$instanceId"
                    params={{
                      taskId: String(task.id),
                      instanceId: String(it.noticeFormInstanceId),
                    }}
                    search={{ from: 'preparation' }}
                    style={{ fontSize: 12 }}
                  >
                    文件審查通知單
                  </Link>
                )}
              </Space>

              {/* 判定備註 / 通知時間 */}
              {it.resultNote && (
                <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '6px 0 0' }}>
                  備註：{it.resultNote}
                </Typography.Paragraph>
              )}
              {it.notifiedAt && (
                <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '2px 0 0' }}>
                  已於 {formatDateTime(it.notifiedAt)} 通知管理組轉達客戶補件
                </Typography.Paragraph>
              )}

              {/* supplement 輪：稽核文件審查補件（序66）——列表 + 每檔替換/刪除 + 上傳 */}
              {it.result === 'supplement' && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    稽核文件審查補件
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                    （稽核員文件審查證據不足／不符之補件，非管理組派案文件點檢補充）
                  </Typography.Text>
                  <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 6 }}>
                    {it.supplementFileIds.map((fid) => (
                      <Space key={fid} size={6} wrap>
                        <FileLink
                          fileId={fid}
                          filename={metas.get(fid)?.filename ?? `檔案 #${fid}`}
                          sizeBytes={metas.get(fid)?.sizeBytes}
                          compact
                        />
                        {canSupplement && (
                          <>
                            {/* 替換：上傳新檔關聯後移除舊檔（序66） */}
                            <Tooltip title="替換此補件檔">
                              <Button
                                size="small"
                                type="text"
                                icon={<RefreshCw size={12} />}
                                onClick={() => handleReplaceSupplement(it.id, fid)}
                              >
                                替換
                              </Button>
                            </Tooltip>
                            {/* 刪除：僅解除本輪補件關聯（序66） */}
                            <Button
                              size="small"
                              type="text"
                              danger
                              onClick={() => handleRemoveSupplement(it.id, fid)}
                            >
                              刪除
                            </Button>
                          </>
                        )}
                      </Space>
                    ))}
                    {canSupplement && (
                      <Upload
                        customRequest={makeSupplementUploadRequest(it.id)}
                        showUploadList={false}
                        multiple
                      >
                        <Button size="small" icon={<Paperclip size={12} />}>
                          上傳稽核文件審查補件
                        </Button>
                      </Upload>
                    )}
                    {it.supplementFileIds.length === 0 && !canSupplement && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        尚未收到補件
                      </Typography.Text>
                    )}
                  </Space>
                </div>
              )}
            </div>
          ))}
        </Space>
      )}

      {/* 判定 Modal：通過 / 需補件 + 備註 */}
      <Modal
        title="判定文件審查結果"
        open={decideTarget !== null}
        onOk={() => void handleDecideSubmit()}
        confirmLoading={decideMutation.isPending}
        onCancel={() => {
          setDecideTarget(null);
          decideForm.resetFields();
        }}
        okText="確認判定"
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          「通過」將關閉本輪審查；「需補件」將自動產生【文件審查通知單】並通知管理組轉達客戶補件。
        </Typography.Paragraph>
        <Form form={decideForm} layout="vertical" preserve={false}>
          <Form.Item
            name="result"
            label="審查結果"
            rules={[{ required: true, message: '請選擇審查結果' }]}
          >
            <Radio.Group
              options={[
                { label: '通過', value: 'pass' },
                { label: '需補件', value: 'supplement' },
              ]}
            />
          </Form.Item>
          <Form.Item name="resultNote" label="備註（需補件時建議填寫缺漏說明）">
            <Input.TextArea rows={3} maxLength={1000} showCount placeholder="選填" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
